import {Component, OnInit} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../../core/shared/object/service/observer.service';
import {WidgetData} from '../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../../core/shared/filter/api/filter.criteria';
import {cwWidgetToolbarCreate} from '../cw-case-type-object-component/shared/cw-case-type-widget.util';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Dedicated object component for cw.CwCaseTypeStep — opened on the right (uiMode 'side')
 *   from the Modellierung timeline via the Navigation Service. Shows the step base data (data
 *   widget + edit toggle) plus the three step-bound configuration tables (Approval, BusinessCheck,
 *   ProductBusinessCheck), each filtered by stepBinding and with the step/case-type FK preset
 *   READ-only on create. Renders in a narrow drawer and degrades cleanly to full mode.
 */
@Component({
    selector: 'cw-case-type-step-object-component',
    templateUrl: './cw-case-type-step-object.component.html',
    styleUrls: ['./cw-case-type-step-object.component.scss'],
    standalone: false
})
export class CwCaseTypeStepObjectComponent extends ObjectBaseComponent implements OnInit {

    stepDataWidget: WidgetData;
    stepEditWidget: WidgetData;
    editStep: boolean = false;

    approvalWidget: WidgetData;
    businessCheckWidget: WidgetData;
    productBusinessCheckWidget: WidgetData;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService) {
        super(coreService, messageService, confirmationService, observerService);
    }

    /**
     * Called once the CwCaseTypeStep DTO has loaded. Sets the page title and (re)builds the
     * step-scoped widgets. G2.5: never calls refreshObject() here.
     */
    onObjectChanged(): void {
        this.setPageTitle(`${this.dto.name} – Schritt-Konfiguration`);
        this.editStep = false;
        this.stepEditWidget = null;
        this.buildStepDataWidget();
        this.buildStepBoundWidgets();
    }

    private get stepId(): number {
        return this.objectIdentifier.objectId;
    }

    private get caseTypeId(): number {
        return this.dto?.caseTypeDtoId;
    }

    private buildStepDataWidget(): void {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeStep base-data read widget (Schritt-Stammdaten).
         */
        this.stepDataWidget = WidgetFactory.createWidgetEntityData(
            'cw.CwCaseTypeStep.widget.data',
            'Schritt-Stammdaten',
            'cw.CwCaseTypeStep',
            this.stepId
        );
    }

    /** Toggles the step base data between the read data widget and the editable object form. */
    toggleEditStep(): void {
        this.editStep = !this.editStep;
        if (this.editStep && !this.stepEditWidget) {
            /**
             * AI: Status "in progress" | Type Widget | SubType Runtime
             * Reason: CwCaseTypeStep editable object form (Schritt-Stammdaten, edit toggle).
             */
            this.stepEditWidget = WidgetFactory.createWidgetObject(
                'cw.CwCaseTypeStep.widget.object.edit',
                'Schritt bearbeiten',
                'cw.CwCaseTypeStep',
                this.stepId
            );
        }
    }

    private buildStepBoundWidgets(): void {
        const stepFilter = [FilterCriteria.create('e.stepBindingId', FilterCriteria.cOperatorEqual, this.stepId)];

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeApproval table bound to this step (FK stepBinding).
         */
        this.approvalWidget = WidgetFactory.createWidgetTableEntityQl(
            'cw.CwCaseTypeApproval.widget.basic.new',
            'Freigaben',
            'cw.CwCaseTypeApproval',
            'Keine Freigaben für diesen Schritt',
            stepFilter,
            []
        );
        this.approvalWidget.functionCallbacks = cwWidgetToolbarCreate([
            {field: 'stepBindingDtoId', value: this.stepId},
            {field: 'caseTypeDtoId', value: this.caseTypeId}
        ]);

        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeBusinessCheck table bound to this step (FK stepBinding).
         */
        this.businessCheckWidget = WidgetFactory.createWidgetTableEntityQl(
            'cw.CwCaseTypeBusinessCheck.widget.basic.new',
            'Geschäftsprüfungen',
            'cw.CwCaseTypeBusinessCheck',
            'Keine Geschäftsprüfungen für diesen Schritt',
            stepFilter,
            []
        );
        this.businessCheckWidget.functionCallbacks = cwWidgetToolbarCreate([
            {field: 'stepBindingDtoId', value: this.stepId},
            {field: 'caseTypeDtoId', value: this.caseTypeId}
        ]);

        // CwCaseTypeProductBusinessCheck's parent FK is caseTypeProduct (not caseType). Preset it
        // only when the step carries its caseTypeProduct (product-level step); otherwise the create
        // form leaves it for the user. stepBinding is always preset.
        const productCheckPresets = [{field: 'stepBindingDtoId', value: this.stepId}];
        if (this.dto?.caseTypeProductDtoId) {
            productCheckPresets.push({field: 'caseTypeProductDtoId', value: this.dto.caseTypeProductDtoId});
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeProductBusinessCheck table bound to this step (FK stepBinding).
         */
        this.productBusinessCheckWidget = WidgetFactory.createWidgetTableEntityQl(
            'cw.CwCaseTypeProductBusinessCheck.widget.basic.new',
            'Produkt-Geschäftsprüfungen',
            'cw.CwCaseTypeProductBusinessCheck',
            'Keine Produkt-Geschäftsprüfungen für diesen Schritt',
            stepFilter,
            []
        );
        this.productBusinessCheckWidget.functionCallbacks = cwWidgetToolbarCreate(productCheckPresets);
    }
}
